/**
 * Wallet connection and management for MineOrDie contract
 */

import {
  createPublicClient,
  createWalletClient,
  custom,
  http,
  type Address,
  type Hex,
  type PublicClient,
  type WalletClient,
  type Chain,
} from 'viem';
import CoinbaseWalletSDK from '@coinbase/wallet-sdk';
import { getChain, CONTRACTS, USDC_ABI, MINE_OR_DIE_ABI, ENTRY_FEE } from './config.js';

// Wallet connection state
let walletClient: WalletClient | null = null;
let publicClient: PublicClient | null = null;
let connectedAddress: Address | null = null;
let coinbaseWallet: CoinbaseWalletSDK | null = null;
let currentChain: Chain | null = null;

// Admin wallet address (lowercase for comparison)
const ADMIN_WALLET = '0xcd81a01160780d28773ba0771c708ed3312b9c67' as Address;

/**
 * Initialize Coinbase Wallet SDK
 */
function initCoinbaseWallet(): CoinbaseWalletSDK {
  if (!coinbaseWallet) {
    coinbaseWallet = new CoinbaseWalletSDK({
      appName: 'Mine or Die',
      appLogoUrl: '/logo.png',
    });
  }
  return coinbaseWallet;
}

/**
 * Check if wallet is connected
 */
export function isWalletConnected(): boolean {
  return connectedAddress !== null;
}

/**
 * Get connected wallet address
 */
export function getWalletAddress(): Address | null {
  return connectedAddress;
}

/**
 * Check if connected wallet is the admin wallet
 */
export function isAdmin(): boolean {
  if (!connectedAddress) return false;
  return connectedAddress.toLowerCase() === ADMIN_WALLET.toLowerCase();
}

/**
 * Connect wallet using Coinbase Wallet SDK
 */
export async function connectWallet(): Promise<Address> {
  const chain = getChain();
  const sdk = initCoinbaseWallet();

  // Create provider using Coinbase Wallet
  const provider = sdk.makeWeb3Provider();

  // Request accounts
  const accounts = (await provider.request({
    method: 'eth_requestAccounts',
  })) as Address[];

  if (!accounts || accounts.length === 0) {
    throw new Error('No accounts returned from wallet');
  }

  connectedAddress = accounts[0];
  currentChain = chain;

  // Create viem clients
  walletClient = createWalletClient({
    account: connectedAddress,
    chain,
    transport: custom(provider),
  });

  publicClient = createPublicClient({
    chain,
    transport: http(),
  });

  // Switch to correct chain if needed
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: `0x${chain.id.toString(16)}` }],
    });
  } catch (switchError: any) {
    // Chain not added, try to add it
    if (switchError.code === 4902) {
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: `0x${chain.id.toString(16)}`,
            chainName: chain.name,
            nativeCurrency: chain.nativeCurrency,
            rpcUrls: [chain.rpcUrls.default.http[0]],
            blockExplorerUrls: chain.blockExplorers
              ? [chain.blockExplorers.default.url]
              : undefined,
          },
        ],
      });
    }
  }

  console.log('Wallet connected:', connectedAddress);
  return connectedAddress;
}

/**
 * Disconnect wallet
 */
export function disconnectWallet(): void {
  // Clear Coinbase Wallet SDK cached connection
  if (coinbaseWallet) {
    try {
      // Reset the SDK to clear cached provider
      coinbaseWallet = null;
    } catch (e) {
      console.warn('Error resetting Coinbase Wallet SDK:', e);
    }
  }

  // Clear localStorage items that wallets use to persist connection
  try {
    // Coinbase Wallet
    localStorage.removeItem('-walletlink:https://www.walletlink.org:version');
    localStorage.removeItem('-walletlink:https://www.walletlink.org:session:id');
    localStorage.removeItem('-walletlink:https://www.walletlink.org:session:secret');
    localStorage.removeItem('-walletlink:https://www.walletlink.org:session:linked');
    // Clear any wagmi/wallet connect state
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('wagmi') || key.startsWith('wc@') || key.startsWith('-walletlink')) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.warn('Error clearing localStorage:', e);
  }

  walletClient = null;
  publicClient = null;
  connectedAddress = null;
  currentChain = null;
  console.log('Wallet disconnected');
}

/**
 * Get USDC balance in wallet (not in game contract)
 */
export async function getUSDCBalance(): Promise<bigint> {
  if (!publicClient || !connectedAddress || !CONTRACTS.usdc) {
    return 0n;
  }

  const balance = await publicClient.readContract({
    address: CONTRACTS.usdc,
    abi: USDC_ABI,
    functionName: 'balanceOf',
    args: [connectedAddress],
  });

  return balance;
}

/**
 * Get player's game balance (funded in MineOrDie contract)
 */
export async function getGameBalance(): Promise<bigint> {
  if (!publicClient || !connectedAddress || !CONTRACTS.mineOrDie) {
    return 0n;
  }

  const balance = await publicClient.readContract({
    address: CONTRACTS.mineOrDie,
    abi: MINE_OR_DIE_ABI,
    functionName: 'balances',
    args: [connectedAddress],
  });

  return balance;
}

/**
 * Get player's available balance (balance - wagered)
 */
export async function getAvailableGameBalance(): Promise<bigint> {
  if (!publicClient || !connectedAddress || !CONTRACTS.mineOrDie) {
    return 0n;
  }

  const available = await publicClient.readContract({
    address: CONTRACTS.mineOrDie,
    abi: MINE_OR_DIE_ABI,
    functionName: 'availableBalance',
    args: [connectedAddress],
  });

  return available;
}

/**
 * Get player's currently wagered amount (locked in active matches)
 */
export async function getWageredAmount(): Promise<bigint> {
  if (!publicClient || !connectedAddress || !CONTRACTS.mineOrDie) {
    return 0n;
  }

  const wagered = await publicClient.readContract({
    address: CONTRACTS.mineOrDie,
    abi: MINE_OR_DIE_ABI,
    functionName: 'currentlyWagered',
    args: [connectedAddress],
  });

  return wagered;
}

/**
 * Check USDC allowance for MineOrDie contract
 */
export async function getUSDCAllowance(): Promise<bigint> {
  if (!publicClient || !connectedAddress || !CONTRACTS.usdc || !CONTRACTS.mineOrDie) {
    return 0n;
  }

  const allowance = await publicClient.readContract({
    address: CONTRACTS.usdc,
    abi: USDC_ABI,
    functionName: 'allowance',
    args: [connectedAddress, CONTRACTS.mineOrDie],
  });

  return allowance;
}

/**
 * Approve USDC spending for MineOrDie contract
 */
export async function approveUSDC(amount: bigint): Promise<Hex> {
  if (!walletClient || !connectedAddress || !CONTRACTS.usdc || !CONTRACTS.mineOrDie || !currentChain) {
    throw new Error('Wallet not connected or contracts not configured');
  }

  const hash = await walletClient.writeContract({
    chain: currentChain,
    account: connectedAddress,
    address: CONTRACTS.usdc,
    abi: USDC_ABI,
    functionName: 'approve',
    args: [CONTRACTS.mineOrDie, amount],
  });

  console.log('USDC approval tx:', hash);

  // Wait for confirmation
  if (publicClient) {
    await publicClient.waitForTransactionReceipt({ hash });
  }

  return hash;
}

/**
 * Fund game account with USDC
 * This transfers USDC from wallet to the MineOrDie contract
 */
export async function fundGameAccount(amount: bigint): Promise<Hex> {
  if (!walletClient || !connectedAddress || !CONTRACTS.mineOrDie || !currentChain) {
    throw new Error('Wallet not connected');
  }

  // Check allowance first
  const allowance = await getUSDCAllowance();
  if (allowance < amount) {
    console.log('Insufficient allowance, approving USDC...');
    await approveUSDC(amount);
  }

  // Fund
  const hash = await walletClient.writeContract({
    chain: currentChain,
    account: connectedAddress,
    address: CONTRACTS.mineOrDie,
    abi: MINE_OR_DIE_ABI,
    functionName: 'fund',
    args: [amount],
  });

  console.log('Fund tx:', hash);

  // Wait for confirmation
  if (publicClient) {
    await publicClient.waitForTransactionReceipt({ hash });
  }

  return hash;
}

/**
 * Withdraw USDC from game account back to wallet
 */
export async function withdrawFromGame(amount: bigint): Promise<Hex> {
  if (!walletClient || !connectedAddress || !CONTRACTS.mineOrDie || !currentChain) {
    throw new Error('Wallet not connected');
  }

  const hash = await walletClient.writeContract({
    chain: currentChain,
    account: connectedAddress,
    address: CONTRACTS.mineOrDie,
    abi: MINE_OR_DIE_ABI,
    functionName: 'withdraw',
    args: [amount],
  });

  console.log('Withdraw tx:', hash);

  // Wait for confirmation
  if (publicClient) {
    await publicClient.waitForTransactionReceipt({ hash });
  }

  return hash;
}

/**
 * Check if player has enough balance to play
 */
export async function canPlay(wagerAmount: bigint = ENTRY_FEE): Promise<boolean> {
  const available = await getAvailableGameBalance();
  return available >= wagerAmount;
}

/**
 * Format USDC amount for display
 */
export function formatUSDC(amount: bigint): string {
  const dollars = Number(amount) / 1_000_000;
  return `$${dollars.toFixed(2)}`;
}

// Legacy exports for backwards compatibility (deprecated - use new names)
export async function stakeToMatch(_escrowAddress: Address): Promise<Hex> {
  // For legacy code - fund with entry fee if not already funded
  const available = await getAvailableGameBalance();
  if (available < ENTRY_FEE) {
    return await fundGameAccount(ENTRY_FEE);
  }
  // Already funded, return empty tx hash
  return '0x' as Hex;
}

export async function claimRewards(_escrowAddress: Address): Promise<Hex> {
  // For legacy code - withdraw all available balance
  const available = await getAvailableGameBalance();
  if (available > 0n) {
    return await withdrawFromGame(available);
  }
  return '0x' as Hex;
}

export async function hasStaked(_escrowAddress: Address): Promise<boolean> {
  return await canPlay();
}

export async function getClaimableReward(_escrowAddress: Address): Promise<bigint> {
  return await getAvailableGameBalance();
}

// ============================================
// Admin Functions (contract reads)
// ============================================

/**
 * Get accumulated dev fees from the contract
 */
export async function getAccumulatedDevFees(): Promise<bigint> {
  if (!publicClient || !CONTRACTS.mineOrDie) {
    return 0n;
  }

  try {
    const fees = await publicClient.readContract({
      address: CONTRACTS.mineOrDie,
      abi: MINE_OR_DIE_ABI,
      functionName: 'accumulatedDevFees',
    });
    return fees;
  } catch (e) {
    console.error('Error reading dev fees:', e);
    return 0n;
  }
}

/**
 * Get total USDC balance in the contract
 */
export async function getContractUSDCBalance(): Promise<bigint> {
  if (!publicClient || !CONTRACTS.usdc || !CONTRACTS.mineOrDie) {
    return 0n;
  }

  try {
    const balance = await publicClient.readContract({
      address: CONTRACTS.usdc,
      abi: USDC_ABI,
      functionName: 'balanceOf',
      args: [CONTRACTS.mineOrDie],
    });
    return balance;
  } catch (e) {
    console.error('Error reading contract balance:', e);
    return 0n;
  }
}

/**
 * Get current dev fee basis points (500 = 5%)
 */
export async function getDevFeeBps(): Promise<number> {
  if (!publicClient || !CONTRACTS.mineOrDie) {
    return 500; // Default 5%
  }

  try {
    const bps = await publicClient.readContract({
      address: CONTRACTS.mineOrDie,
      abi: MINE_OR_DIE_ABI,
      functionName: 'devFeeBps',
    });
    return Number(bps);
  } catch (e) {
    console.error('Error reading dev fee bps:', e);
    return 500;
  }
}

/**
 * Get a player's balance in the contract
 */
export async function getPlayerBalance(playerAddress: Address): Promise<bigint> {
  if (!publicClient || !CONTRACTS.mineOrDie) {
    return 0n;
  }

  try {
    const balance = await publicClient.readContract({
      address: CONTRACTS.mineOrDie,
      abi: MINE_OR_DIE_ABI,
      functionName: 'balances',
      args: [playerAddress],
    });
    return balance;
  } catch (e) {
    console.error('Error reading player balance:', e);
    return 0n;
  }
}
